import React, { useState } from 'react';
import { SxStyle } from '../../../../types';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { useSnackbar } from 'notistack';
import isCidr from 'is-cidr';
import { ExternalIp } from '../types/index';
import { useStringsContext } from '../../../../contexts/strings';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

const styles: SxStyle = {
  root: (theme) => ({
    width: '100%',
    backgroundColor: theme.palette.background.paper,
  }),
  addButton: {
    maxWidth: '200px',
  },
  saveButton: {},
  cancelButton: {
    maxWidth: '200px',
  },
  removeButton: {
    marginLeft: '20px',
  },
};

export default function ExternalIpList({
  externalIpList,
  submitNewExternalIpList,
}: {
  externalIpList: ExternalIp[];
  submitNewExternalIpList: (list: ExternalIp[]) => void;
}) {
  const { strings } = useStringsContext();
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [externalIpListArr, setExternalIpListArr] = useState<ExternalIp[]>(externalIpList);
  const [newExternalIp, setNewExternalIp] = useState<ExternalIp>({
    key: '',
    ipRange: '',
  });

  const startAddNewExternalIp = () => {
    setOpen(true);
  };

  const addToExternalIpList = () => {
    const validationResult =
      isCidr(newExternalIp.ipRange) &&
      !(externalIpListArr.findIndex((x) => x.ipRange === newExternalIp.ipRange) >= 0);
    const validKeyResult = !(externalIpListArr.findIndex((x) => x.key === newExternalIp.key) >= 0);

    if (validationResult && validKeyResult) {
      const newExternalIpList = externalIpListArr.concat([newExternalIp]);
      setExternalIpListArr(newExternalIpList);
      setNewExternalIp({ key: '', ipRange: '' });
      submitNewExternalIpList(newExternalIpList);
      setOpen(false);
    }

    if (!validationResult && validKeyResult) {
      enqueueSnackbar(strings.wifi.incorrectFormatIpRange, {
        variant: 'error',
      });
    }

    if (validationResult && !validKeyResult) {
      enqueueSnackbar(strings.wifi.incorrectNameKey, {
        variant: 'error',
      });
    }

    if (!validationResult && !validKeyResult) {
      enqueueSnackbar(strings.wifi.incorrectNameKeyFormatIpRange, {
        variant: 'error',
      });
    }
  };

  const cancelAddNewExternalIp = () => {
    setOpen(false);
    setNewExternalIp({ key: '', ipRange: '' });
  };

  const handleNewExternalIpChange = (e: any) => {
    setNewExternalIp({ key: newExternalIp.key, ipRange: e.target.value });
  };

  const handleNewExternalIpNameChange = (e: any) => {
    setNewExternalIp({ key: e.target.value, ipRange: newExternalIp.ipRange });
  };

  const removeExternalIpFromList = (externalIp: ExternalIp) => {
    const modifiedExternalIpList = externalIpListArr.filter(
      (c) => c.ipRange !== externalIp.ipRange && c.key !== externalIp.key
    );
    setExternalIpListArr(modifiedExternalIpList);
  };

  return (
    <Grid container>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>IP range</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {externalIpListArr.map((i) => (
              <TableRow key={i.ipRange} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell>{i.key}</TableCell>
                <TableCell>{i.ipRange}</TableCell>
                <TableCell align="right">
                  <DeleteIcon color="error" onClick={() => removeExternalIpFromList(i)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Grid item xs padding={3}>
        {open && (
          <Stack spacing={4} direction={'column'}>
            <TextField
              fullWidth
              onChange={handleNewExternalIpNameChange}
              id="newExternalIpName"
              label={strings.wifi.labelNewExternalIpName}
            />
            <TextField
              fullWidth
              onChange={handleNewExternalIpChange}
              id="newExternalIp"
              label={strings.wifi.labelNewExternalIp}
            />
            <Stack spacing={4} direction={{ xs: 'column', sm: 'row' }}>
              <Button
                sx={styles.cancelButton}
                variant="contained"
                onClick={cancelAddNewExternalIp}
                color="primary"
              >
                {strings.actions.cancel}
              </Button>
              <Button
                sx={styles.addButton}
                onClick={addToExternalIpList}
                variant="contained"
                color="primary"
                startIcon={<AddCircleIcon />}
              >
                {strings.wifi.addToList}
              </Button>
            </Stack>
          </Stack>
        )}
        {!open && (
          <Button
            variant="contained"
            onClick={startAddNewExternalIp}
            color="primary"
            startIcon={<AddCircleIcon />}
          >
            {strings.wifi.addNewExternalIp}
          </Button>
        )}
      </Grid>
    </Grid>
  );
}
